import React, { useState, useEffect } from 'react';
import './chatbot.css';
import { useXAgent, useXChat, Sender, Bubble } from '@ant-design/x';
import markdownit from 'markdown-it';
import { Typography, Space, Spin, theme } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet';
import Robot from '../images/robot.png';
import { useChatbot } from  '../hooks/useChatbot.js'
import config from '../config.js';

const Demo = () => {
  const uname = localStorage.getItem("uname");
  const uid = localStorage.getItem("uid");
  const { getConvo, getConvoList, storeConvo } = useChatbot();
  const isProcessingRef = React.useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [agent] = useXAgent({
    request: async (info, callbacks) => {
      const { message } = info;

      const { onSuccess, onUpdate, onError } = callbacks;

      setIsProcessing(true)
      isProcessingRef.current = true;
      let content = '';

      try {
        onUpdate(content);
        const response = await fetch(`${config.fastApiUrl}?query=${encodeURIComponent(message)}`, {method: 'GET',});

	//working code 
	if (!response.body) {
        	console.error("No response body");
		setIsProcessing(false);
		isProcessingRef.current = false;
        	return;
        }
        const stream = response.body.pipeThrough(new TextDecoderStream());
        for await (const chunk of stream) {
	  if (!isProcessingRef.current){
	    return;
	  }
          content += chunk;
          onUpdate(content);
        }

        onSuccess(content);

	setMessages((prev) => {
		const updated = [...prev];
		const lastIndex = updated.length -1;
		if (updated[lastIndex]){
			updated[lastIndex].status = "success";
		}
		return updated;
	});



      } catch (error) {
	if(isProcessingRef.current) onError(error);
        console.error('Error during request:', error);
        if (onError) onError(error);
      } finally {
	  setIsProcessing(false);
	  isProcessingRef.current = false;
	}
    },
  });

  const {
    // use to send message
    onRequest,
    // use to render messages
    messages,
    setMessages
  } = useXChat({ agent });

  const md = markdownit({
    html: true,
    breaks: true,
    linkify: true,
  });

  const { token } = theme.useToken();

  const renderMarkdown = (content) => (
    <Typography>
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: used in demo */}
      <div
        dangerouslySetInnerHTML={{
          __html: md.render(content),
        }}
      />
    </Typography>
  );


  // Always show welcome message as first item, then map stored messages
  const welcomeMessage = {
    key: 'welcome',
    content: `Hello! I'm **ENT Companion**, a specialized chatbot designed to help you understand Zenker's Diverticulum and related ENT conditions.\n\n**I can help you with:**\n- Understanding Zenker's Diverticulum and its symptoms\n- Treatment options and surgical procedures\n- Post-operative care and recovery\n- Dietary recommendations and lifestyle adjustments\n- When to seek medical attention\n\n*Please note: This chatbot provides educational information to help you make informed decisions. It is not a substitute for professional medical advice. Always consult with your healthcare provider for personalized medical guidance.*`,
    role: 'ai',
    loading: false
  };

  const items = [
    welcomeMessage,
    ...messages.map(({ message, id }, i) => {
      const isAI = !!(i % 2); // AI messages are at odd indices
      return {
        key: id,
        content: message,
        role: isAI ? 'ai' : 'user',
        loading: isAI && (!message || message === ""),
      };
    })
  ];
  
  const roles = {
    ai: {
      placement: 'start',
      avatar: {
        src: <img src={Robot} alt="avatar" />,
        style: {
          background: '#fde3cf',
        },
      },
      style: {
        maxWidth: 600,
        marginInlineEnd: 44,
      },
      loadingRender: () => (
        <Space>
          <Spin size="small" />
          AI is typing...
        </Space>
      ),
      messageRender: renderMarkdown, // Add markdown rendering for AI messages
    },
    user: {
      placement: 'end',
      avatar: {
        icon: <UserOutlined />,
        style: {
          background: '#87d068',
        },
      },
      messageRender: renderMarkdown, // Add markdown rendering for user messages
    },
  };
  const [conversations,setConversations] = useState([]);
  const [cid, setCid] = useState('');

  //When the cid changes it means you are either starting a new conversation or saving an old one, so refresh conversation history
  useEffect(() => {
    //console.log("New cid ",cid);
    getConvoList(uname).then(setConversations);
  }, [cid]); // This runs getConvo() whenever cid changes

  //When you click on a conversation in the side bar, this gets that conversation into the main screen
  const handleActiveChange = (conversationid) => {
    if(isProcessing){
	alert("Please wait for the current response before switching conversations.");
	return;
    }

    setCid(conversationid);

    if (conversationid !== "") {
	setMessages([]);
        getConvo(uname, conversationid).then((messages) => {
        setIndex(messages.length);
        setMessages(messages);
      });
    } else {
      // New conversation
      setMessages([]);
      setIndex(0);
    }

  };

  /*
  const formatter = new Intl.DateTimeFormat("en-US", { 
    year: "numeric", 
    month: "long", 
    day: "numeric",
    hour: "2-digit", 
    minute: "2-digit", 
    second: "2-digit",
    hour12: true, // Set to true if you prefer AM/PM format
    timeZone: "UTC" // Ensures consistent timezone handling
});
*/

  const listRef = React.useRef(null);
  const [text, setText] = React.useState('');

  const handleSubmit = (text) => {
    onRequest(text); // Send the value to the request handler
    setText(''); // Clear the input field after submission
  };

  // Scroll to bottom whenever messages change
  // STORE CONVERSATIONS
  const [index,setIndex] = useState(0);
  // Store messages in conversation
  useEffect(() => {
    console.log("UseEffect messages ", messages);
    console.log("Index ",index);

    //increment will count and update the number of messages stored
    let increment = 0;
    messages.forEach(async (msg, i) => {
      if (i >= index && (msg.status === "local" || msg.status === "success") && msg.message) {
        //console.log("Going to store: ",msg.message);
        increment++;
	console.log(`cid: ${cid}`);
	      const newCid = await storeConvo(uid, cid, msg.message);
        if (newCid && !cid) setCid(newCid);
      }
    });
    setIndex(index+increment);

    // Auto-scroll to bottom
    if (listRef.current) {
      const listElement = listRef.current;
      listElement.scrollTo({
        top: listElement.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  return (
      <div>
        <div>
          <Helmet>
            <title>Chatbot | ENT Chatbot</title>
          </Helmet>
        </div>
        
        <div class = "chatbot-container">
          <div class = 'chatbox'>
            <Bubble.List 
            	ref={listRef}
            	items={items}
            	roles={roles}
            	style={{overflow: 'auto'}}
              className='bubble'
            	autoScroll
            />
            <Sender 
              placeholder="Ask me a question..."
            	value={text}
            	onChange={setText}
            	onSubmit={handleSubmit} 
              className='sender'
              disabled={isProcessing}
            />
            <div className='chat-disclaimer'>
              Educational information only • Not medical advice • Consult your healthcare provider
            </div>
	        </div>
        </div>
      </div>
  );
};

export default Demo;
